import { lazy, Suspense } from 'react'

import { createFileRoute } from '@tanstack/react-router'

import { actionQueries } from '@/features/actions/queries'
import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import { SectionTitle } from '@/features/app-shell/section'
import { graylogQueries } from '@/features/graylog/queries'
import { playbookQueries } from '@/features/playbooks/queries'

import { ROLES } from '@/shared/auth/constants'
import { getCurrentTenant } from '@/shared/auth/store'
import { roleAuthGuard } from '@/shared/auth/utils/router'

import BackLink from '@/components/common/back-link'
import { ErrorBoundary } from '@/components/common/error-boundary'
import { TableSkeleton } from '@/components/skeletons/table-skeleton'
import { Skeleton } from '@/components/ui/skeleton'

const AutomationSettingsDetailsForm = lazy(
	() => import('@/features/graylog/automation-settings/component/form'),
)

export const Route = createFileRoute(
	'/_dashboard/graylog/automation-settings/edit/$id',
)({
	beforeLoad: () => roleAuthGuard(ROLES.Analyst),

	loader: async ({ context: { queryClient }, params }) => {
		const { id } = params

		const tenant_id = getCurrentTenant() as string

		// Prefetch list of events and responses
		if (tenant_id) {
			queryClient.prefetchQuery(actionQueries.list(tenant_id))
			queryClient.prefetchQuery(graylogQueries.events(tenant_id))
			queryClient.prefetchQuery(playbookQueries.list(tenant_id))
		}

		// Fetch event details
		return await queryClient.ensureQueryData({
			...graylogQueries.automationDetail(tenant_id, id),
			revalidateIfStale: true,
		})
	},

	head: () => ({
		meta: [
			{
				title: 'Automation Settings | Graylog | BitLyft AIR®',
			},
		],
	}),

	component: AutomationSettingsEvent,
	pendingComponent: AutomationSettingsEventFallback,
	errorComponent: RouteErrorComponent,
})

function AutomationSettingsEvent() {
	const { id } = Route.useParams()
	const { name } = Route.useLoaderData()

	return (
		<div className="flex w-full flex-col items-start justify-start gap-2">
			<div className="my-2 flex items-start justify-start gap-4">
				<BackLink to="/graylog/automation-settings" />

				<SectionTitle>{name}</SectionTitle>
			</div>

			<ErrorBoundary
				feature="graylog-automation"
				section="edit-form"
				entityId={id}
			>
				<Suspense fallback={<TableSkeleton rows={10} />}>
					<AutomationSettingsDetailsForm ruleId={id} />
				</Suspense>
			</ErrorBoundary>
		</div>
	)
}

function AutomationSettingsEventFallback() {
	return (
		<div className="flex w-full flex-row items-center justify-between">
			<Skeleton className="h-12 w-1/2" />
			<Skeleton className="h-12 w-1/2" />
		</div>
	)
}
